/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { JSX } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { TableDisplayBase } from './TableDisplayBase';
import { tableDisplayStyles } from './themes/TableDisplayStyles.css';
import { TableDisplayProps, TableDisplayHandle } from './Table.types';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { classNames } from '../utils/UNSAFE_classNames';
import { DimensionProps, dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';

/**
 * Props specific to non-virtualized TableDisplay
 */
type NonVirtualizedTableProps<K, D> = {
  /**
   * The data that is used to render each item in TableDisplay.  If the value is null, then TableDisplay will show nothing.
   */
  data?: D[];
  /**
   * A callback function to extract the key which is used to identify the item.
   */
  getRowKey: (data: D) => K;
};

/**
 * Props for the TableDisplay Component
 */
export type Props<K, D, C extends string> = TableDisplayProps<K, D, C> &
  NonVirtualizedTableProps<K, D> &
  DimensionProps &
  TestIdProps;

const mergedInterpolations = mergeInterpolations<DimensionProps>([
  ...Object.values(dimensionInterpolations)
]);

/**
 * Component that renders data in rows of columns.
 * This component is non-virtualized and renders all rows provided in the data prop.
 */
export const TableDisplay = forwardRef(
  <K extends string | number, D, C extends string>(
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      getAccessibleRowHeaders,
      children,
      columns,
      columnOrder,
      columnWidths,
      viewportConfig,
      layout = 'contents',
      data,
      getRowKey,
      gridlines = { horizontal: 'visible', vertical: 'hidden' },
      sortCriterion = [],
      onSortCriterionChange,
      horizontalScrollPositionOverride,
      verticalScrollPositionOverride,
      noDataRenderer,
      scrollerRef,
      testId,
      ...dimensions
    }: Props<K, D, C>,
    ref?: ForwardedRef<TableDisplayHandle<K, C>>
  ) => {
    const { ...mergedStyles } = mergedInterpolations(dimensions);
    const isExternalScroller = viewportConfig != null;
    const classArray = [tableDisplayStyles.base];
    classArray.push(
      isExternalScroller ? tableDisplayStyles.externalScroller : tableDisplayStyles.internalScroller
    );
    const tableDisplayClasses = classNames(classArray);

    return (
      <div class={tableDisplayClasses} style={mergedStyles}>
        <TableDisplayBase<K, D, C>
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          getAccessibleRowHeaders={getAccessibleRowHeaders}
          children={children}
          columns={columns}
          columnOrder={columnOrder}
          columnWidths={columnWidths}
          viewportConfig={viewportConfig}
          layout={layout}
          data={data}
          getRowKey={getRowKey}
          gridlines={gridlines}
          horizontalScrollPositionOverride={horizontalScrollPositionOverride}
          verticalScrollPositionOverride={verticalScrollPositionOverride}
          sortCriterion={sortCriterion}
          onSortCriterionChange={onSortCriterionChange}
          noDataRenderer={noDataRenderer}
          scrollerRef={scrollerRef}
          testId={testId}
          ref={ref}
        />
      </div>
    );
  }
) as <K extends string | number, D, C extends string>(
  p: Props<K, D, C> & { ref?: ForwardedRef<TableDisplayHandle<K, C>> }
) => JSX.Element;
